/**
 * Unified layer list component used by the Layers panel.
 *
 * Provides a consistent, touch-friendly Photoshop-style layer panel with:
 *  - Header with label + action buttons (add, clear, delete) that operate
 *    on the currently selected layer
 *  - Layer rows with name + visibility toggle (+ optional reorder buttons)
 *  - Footer with opacity slider for the selected layer
 */

import {
  ChevronDown,
  ChevronUp,
  Eraser,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import type { CSSProperties, RefObject } from "react";
import { IconButton } from "./Button.tsx";
import { ColorPicker } from "./ColorPicker.tsx";
import { Slider } from "./Slider.tsx";
import { getSelectableRowStyle } from "./selectableRow.ts";
import { SectionLabel, useInlineRename } from "./shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
  RADIUS_SM,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimum shape a layer must satisfy. */
export interface BaseLayer {
  id: string;
  name: string;
  opacity: number;
  visible: boolean;
}

export interface LayerListProps<T extends BaseLayer> {
  /** Layers in display order (top-to-bottom as rendered). Consumers should pre-reverse if needed. */
  layers: T[];
  /** Currently selected layer ID. */
  activeLayerId: string | null;
  /** Called when a layer row is clicked. */
  onSelectLayer: (id: string) => void;
  /** Visibility toggle callback. */
  onSetVisible: (id: string, visible: boolean) => void;
  /** Opacity change callback. */
  onSetOpacity: (id: string, opacity: number) => void;
  /** If provided, shows the + button in the header. */
  onAdd?: () => void;
  /** If provided, shows a trash button in the header (operates on active layer). */
  onDelete?: (id: string) => void;
  /** If provided, shows an eraser (clear) button in the header (operates on active layer). */
  onClear?: (id: string) => void;
  /** If provided, shows reorder chevrons per row. */
  onMove?: (id: string, direction: "up" | "down") => void;
  /** Guard for enabling the delete button. Defaults to `() => true`. */
  canDelete?: (layers: T[]) => boolean;
  /** Default value for the opacity slider double-tap reset. */
  opacityDefault?: number;
  /** Message shown when the layers array is empty. */
  emptyMessage?: string;
  /** Header label. Defaults to "Layers". */
  label?: string;
  /** Ref forwarded to the "+" add button so consumers can anchor popovers to it. */
  addButtonRef?: RefObject<HTMLButtonElement | null>;
  /** If provided, enables inline rename via double-click or pencil icon. */
  onRename?: (id: string, name: string) => void;
  /** If provided with onSetLayerColor, shows a per-row color swatch. */
  getLayerColor?: (layer: T) => string;
  /** Called when the row swatch changes color. */
  onSetLayerColor?: (id: string, color: string) => void;
  /** Called once when a row swatch color edit starts. */
  onBeginLayerColorEdit?: (id: string) => void;
  /** Called once when a row swatch color edit ends. */
  onEndLayerColorEdit?: () => void;
  /** Optional guard for row swatch interactivity. */
  isLayerColorDisabled?: (layer: T) => boolean;
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const ROW_BASE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  borderRadius: RADIUS_SM,
  cursor: "pointer",
  minHeight: 44,
};

const NAME_BASE: CSSProperties = {
  flex: 1,
  fontSize: FONT_SIZE_SMALL,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// ---------------------------------------------------------------------------
// Inline-rename layer name
// ---------------------------------------------------------------------------

function LayerName({
  layer,
  isActive,
  onRename,
}: {
  layer: BaseLayer;
  isActive: boolean;
  onRename?: (id: string, name: string) => void;
}) {
  const { isRenaming, startRenaming, inputProps } = useInlineRename(
    layer.name,
    (next) => onRename?.(layer.id, next),
  );

  if (isRenaming) {
    return (
      <input
        {...inputProps}
        type="text"
        style={{
          ...NAME_BASE,
          padding: "1px 4px",
          borderRadius: RADIUS_SM,
          border: `1px solid ${COLOR_ACCENT}`,
          background: COLOR_SURFACE,
          color: COLOR_TEXT,
          fontWeight: isActive ? 500 : 400,
          fontSize: FONT_SIZE_SMALL,
          outline: "none",
        }}
      />
    );
  }

  return (
    <>
      <span
        onDoubleClick={onRename ? (e) => startRenaming(e) : undefined}
        title={onRename ? "Double-click to rename" : undefined}
        style={{
          ...NAME_BASE,
          color: getSelectableRowStyle(isActive, "accent-dim").color,
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {layer.name}
      </span>
      {onRename && (
        <IconButton
          onClick={(e) => startRenaming(e)}
          icon={<Pencil size={12} />}
          label="Rename layer"
          size={22}
          style={{
            color: COLOR_TEXT_SUBTLE,
            opacity: 0.6,
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LayerList<T extends BaseLayer>({
  layers,
  activeLayerId,
  onSelectLayer,
  onSetVisible,
  onSetOpacity,
  onAdd,
  onDelete,
  onClear,
  onMove,
  canDelete,
  opacityDefault = 1,
  emptyMessage,
  label = "Layers",
  addButtonRef,
  onRename,
  getLayerColor,
  onSetLayerColor,
  onBeginLayerColorEdit,
  onEndLayerColorEdit,
  isLayerColorDisabled,
}: LayerListProps<T>) {
  const activeLayer = layers.find((l) => l.id === activeLayerId) ?? null;
  const deleteEnabled =
    onDelete && activeLayer && (canDelete ? canDelete(layers) : true);

  return (
    <div>
      {/* Header — label + action buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 6,
        }}
      >
        <div style={{ flex: 1 }}>
          <SectionLabel>{label}</SectionLabel>
        </div>

        {onClear && (
          <IconButton
            onClick={() => activeLayer && onClear(activeLayer.id)}
            disabled={!activeLayer}
            icon={<Eraser size={14} />}
            label="Clear layer"
            size={28}
            style={{
              color: COLOR_TEXT_SUBTLE,
            }}
          />
        )}

        {onDelete && (
          <IconButton
            onClick={() => deleteEnabled && onDelete(activeLayer!.id)}
            disabled={!deleteEnabled}
            icon={<Trash2 size={14} />}
            label="Delete layer"
            size={28}
            style={{
              color: COLOR_TEXT_SUBTLE,
            }}
          />
        )}

        {onAdd && (
          <IconButton
            ref={addButtonRef}
            onClick={onAdd}
            icon={<Plus size={14} />}
            label="Add layer"
            variant="secondary"
            size={28}
            style={{
              background: COLOR_SURFACE,
              color: COLOR_TEXT_DIM,
            }}
          />
        )}
      </div>

      {/* Layer rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {layers.length === 0 && emptyMessage && (
          <div
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_FAINT,
              padding: "4px 6px",
            }}
          >
            {emptyMessage}
          </div>
        )}

        {layers.map((layer, index) => {
          const isActive = layer.id === activeLayerId;
          const isFirst = index === 0;
          const isLast = index === layers.length - 1;
          return (
            <div
              key={layer.id}
              style={{
                ...ROW_BASE,
                background: getSelectableRowStyle(isActive).background,
              }}
              onClick={() => onSelectLayer(layer.id)}
            >
              {/* Name */}
              <LayerName
                layer={layer}
                isActive={isActive}
                onRename={onRename}
              />

              {/* Layer color */}
              {getLayerColor && onSetLayerColor && (
                <div
                  title="Layer stroke color"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <ColorPicker
                    value={getLayerColor(layer)}
                    onChange={(color) => onSetLayerColor(layer.id, color)}
                    onChangeStart={() => onBeginLayerColorEdit?.(layer.id)}
                    onChangeEnd={onEndLayerColorEdit}
                    disabled={isLayerColorDisabled?.(layer)}
                    size={22}
                  />
                </div>
              )}

              {/* Reorder */}
              {onMove && (
                <div style={{ display: "flex", gap: 0, flexShrink: 0 }}>
                  {!isFirst && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onMove(layer.id, "up");
                      }}
                      icon={<ChevronUp size={14} />}
                      label="Move layer up"
                      size={28}
                      style={{ color: COLOR_TEXT_SUBTLE }}
                    />
                  )}
                  {!isLast && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onMove(layer.id, "down");
                      }}
                      icon={<ChevronDown size={14} />}
                      label="Move layer down"
                      size={28}
                      style={{ color: COLOR_TEXT_SUBTLE }}
                    />
                  )}
                </div>
              )}

              {/* Visibility */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onSetVisible(layer.id, !layer.visible);
                }}
                icon={layer.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                label={layer.visible ? "Hide layer" : "Show layer"}
                size={28}
                style={{
                  color: layer.visible ? COLOR_ACCENT : COLOR_TEXT_SUBTLE,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Footer — opacity slider for active layer */}
      {activeLayer && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 8px 4px",
            marginTop: 4,
            borderTop: `0.5px solid ${COLOR_BORDER}`,
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_SUBTLE,
              flexShrink: 0,
            }}
          >
            Opacity
          </span>
          <Slider
            value={activeLayer.opacity}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onSetOpacity(activeLayer.id, v)}
            defaultValue={opacityDefault}
            dimChromeOnDrag
          />
          <span
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_SUBTLE,
              minWidth: 32,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {Math.round(activeLayer.opacity * 100)}%
          </span>
        </div>
      )}
    </div>
  );
}
